import { Circle, Float } from '@chakra-ui/react';

import classNames from 'classnames/bind';
import { Avatar, SIZES, Flex, ButtonIcon, HStack, Show } from 'shared';
import { HotjarSuppression } from 'shared_DEPRECATED/components/Hotjar';
import { Icon, iconNames } from 'shared_DEPRECATED/components/Icon';
import { Spacers } from 'shared_DEPRECATED/components/Spacer';

import { ChatHeaderMenu } from 'features/chat/components/ChatHeader/Menu';
import { ChatUnreadIndicator } from 'features/chat/components/UnreadIndicator';
import { useChatContext } from 'features/chat/context/chat';
import { useChatInterlocutorContext } from 'features/chat/context/chatInterlocutor';
import { useChatMemberOnlineStatus } from 'features/chat/hooks/useChatMemberStatus';
import { UserInfoName } from 'features/user/components/Info/Name';
import { userInfo } from 'features/user/config/defaults';
import { useIsCoachMode } from 'features/user/hooks/useIsCoachMode';

import styles from 'features/chat/components/Chat.module.css';

const cx = classNames.bind(styles);

type ChatHeaderProps = {
  open: boolean;
  toggleOpen: () => void;
};

export default function ChatHeader({ open, toggleOpen }: ChatHeaderProps) {
  const { email, chatId, picture, name } = useChatInterlocutorContext();
  const { unreadMessagesCount, isChatMarkedAsUnread } = useChatContext();
  const isOnline = useChatMemberOnlineStatus(chatId, email);
  const isUserCoach = useIsCoachMode();

  return (
    <div
      className={cx('chat__header', { 'chat__header--open': open })}
      data-testid="chat-header"
      onClick={toggleOpen}
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        height="100%"
        gap="0.5rem"
      >
        <HotjarSuppression>
          <Flex as="span" alignItems="center" justifyContent="center">
            <Avatar
              name={name}
              size={SIZES.SM}
              src={picture.url}
              data-testid="open-chat-avatar"
            >
              <Float placement="bottom-end" offsetX="1" offsetY="1">
                <Circle
                  bg={isOnline ? 'var(--fgSuccess)' : 'gray'}
                  size="0.5rem"
                  outline="0.2em solid"
                  outlineColor="bg"
                  data-testid={`chat-header-${
                    isOnline ? 'online' : 'offline'
                  }-indicator`}
                />
              </Float>
            </Avatar>
            <Show when={open}>
              <Spacers sizes={['zr zr zr md', 'zr zr xsm zr']}>
                <UserInfoName maxWidth={userInfo.maxWidth}>{name}</UserInfoName>
              </Spacers>
            </Show>
          </Flex>
        </HotjarSuppression>
        <HStack>
          <ChatUnreadIndicator
            unreadMessagesCount={unreadMessagesCount}
            isChatMarkAsUnread={isChatMarkedAsUnread}
            open={open}
          />
          <Show when={isUserCoach}>
            <ChatHeaderMenu />
          </Show>
          <ButtonIcon size="xs">
            <Icon
              name={open ? iconNames.chevronDown : iconNames.chevronUp}
              width="12px"
              height="7px"
            />
          </ButtonIcon>
        </HStack>
      </Flex>
    </div>
  );
}
